import { useState } from 'react';
import type { InterviewInviteAttemptResultDto } from '@sara/api-client';
import { cn } from '@sara/ui/lib/utils';
import { Braces, LayoutList } from 'lucide-react';
import { JsonViewer } from './JsonViewer';
import {
  camelToTitle,
  capitalize,
  formatPrimitive,
  isRatingObject,
  ratingTone,
  type RatingTone,
} from './format';

const TONE_RING: Record<RatingTone, string> = {
  strong: 'bg-success',
  adequate: 'bg-warning',
  weak: 'bg-destructive',
  neutral: 'bg-muted-foreground/40',
};

const TONE_FILL: Record<RatingTone, string> = {
  strong: 'bg-success/15 text-success ring-success/25',
  adequate: 'bg-warning/15 text-warning ring-warning/25',
  weak: 'bg-destructive/10 text-destructive ring-destructive/25',
  neutral: 'bg-muted text-muted-foreground ring-border',
};

const RATING_BARS: Record<RatingTone, number> = {
  strong: 3,
  adequate: 2,
  weak: 1,
  neutral: 0,
};

type ResultView = 'tree' | 'structured';

/**
 * Renders a single attempt's processed result. Defaults to a searchable JSON
 * tree (the raw structured output), with a toggle to the semantic "structured"
 * view (rating cards + definition list). `result` is the attempt's own result,
 * or null when the attempt never produced one.
 */
export function BriefResults({ result }: { result: InterviewInviteAttemptResultDto | null }) {
  const [view, setView] = useState<ResultView>('tree');

  if (!result) {
    return <p className="text-sm italic text-muted-foreground">Results not yet available.</p>;
  }
  if (Object.keys(result.result).length === 0) {
    return <p className="text-sm italic text-muted-foreground">No result data available.</p>;
  }

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <ViewToggle view={view} onChange={setView} />
      </div>
      {view === 'tree' ? (
        <JsonViewer value={result.result} />
      ) : (
        <StructuredResults result={result} />
      )}
    </div>
  );
}

function ViewToggle({
  view,
  onChange,
}: {
  view: ResultView;
  onChange: (view: ResultView) => void;
}) {
  return (
    <div className="inline-flex items-center gap-0.5 rounded-lg border border-border bg-card p-0.5">
      <ToggleButton active={view === 'tree'} onClick={() => onChange('tree')}>
        <Braces className="h-3.5 w-3.5" strokeWidth={1.8} />
        Tree
      </ToggleButton>
      <ToggleButton active={view === 'structured'} onClick={() => onChange('structured')}>
        <LayoutList className="h-3.5 w-3.5" strokeWidth={1.8} />
        Structured
      </ToggleButton>
    </div>
  );
}

function ToggleButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-[12px] font-medium transition-colors',
        active
          ? 'bg-primary/10 text-primary'
          : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
      )}
    >
      {children}
    </button>
  );
}

/**
 * Semantic rendering — rating objects as scored cards, everything else as a
 * definition list.
 */
function StructuredResults({ result }: { result: InterviewInviteAttemptResultDto }) {
  const entries = Object.entries(result.result);
  const ratings = entries.filter(([, v]) => isRatingObject(v));
  const others = entries.filter(([, v]) => !isRatingObject(v));

  return (
    <div className="space-y-3">
      {ratings.length > 0 && (
        <div className="grid gap-3 lg:grid-cols-2">
          {ratings.map(([key, value]) => {
            const r = value as { rating: string; summary: string };
            const tone = ratingTone(r.rating);
            return (
              <article key={key} className="space-y-3 rounded-xl border border-border bg-card p-4">
                <header className="flex items-start justify-between gap-3">
                  <h3 className="text-[14px] font-semibold leading-tight text-foreground">
                    {camelToTitle(key)}
                  </h3>
                  <span
                    className={
                      'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset ' +
                      TONE_FILL[tone]
                    }
                  >
                    <span className={'h-1.5 w-1.5 rounded-full ' + TONE_RING[tone]} aria-hidden />
                    {capitalize(r.rating)}
                  </span>
                </header>
                <RatingBars tone={tone} />
                <p className="text-[13px] leading-relaxed text-muted-foreground whitespace-pre-line">
                  {r.summary}
                </p>
              </article>
            );
          })}
        </div>
      )}

      {others.length > 0 && (
        <div className="rounded-xl border border-border bg-card">
          <dl>
            {others.map(([key, value], i) => (
              <div
                key={key}
                className={
                  'grid min-w-0 grid-cols-1 gap-x-4 gap-y-2 px-5 py-4 lg:grid-cols-[12rem_minmax(0,1fr)] ' +
                  (i > 0 ? 'border-t border-border' : '')
                }
              >
                <dt className="text-[11px] font-medium uppercase tracking-[0.1em] text-muted-foreground">
                  {camelToTitle(key)}
                </dt>
                <dd className="min-w-0 text-[13px] text-foreground/90">
                  <BriefValue value={value} />
                </dd>
              </div>
            ))}
          </dl>
        </div>
      )}
    </div>
  );
}

function RatingBars({ tone }: { tone: RatingTone }) {
  const filled = RATING_BARS[tone];
  return (
    <div className="flex h-1 gap-1">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className={'flex-1 rounded-full ' + (i < filled ? TONE_RING[tone] : 'bg-secondary')}
          aria-hidden
        />
      ))}
    </div>
  );
}

function isPrimitiveLike(value: unknown): boolean {
  return (
    value === null ||
    value === undefined ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  );
}

function BriefValue({ value }: { value: unknown }) {
  if (value === null || value === undefined) {
    return <span className="italic text-muted-foreground">—</span>;
  }
  if (typeof value === 'string') {
    // whitespace-pre-line preserves the \n / paragraph breaks that summary-style
    // fields (e.g. conversation_summary) carry, while still wrapping long lines.
    return <p className="leading-relaxed break-words whitespace-pre-line">{value}</p>;
  }
  if (typeof value === 'boolean') return <span>{value ? 'Yes' : 'No'}</span>;
  if (typeof value === 'number') return <span className="tabular-nums">{value}</span>;

  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="italic text-muted-foreground">No entries</span>;

    if (value.every(isPrimitiveLike)) {
      return (
        <ul className="flex flex-wrap gap-1.5">
          {value.map((item, idx) => (
            <li
              key={idx}
              className="inline-flex max-w-full items-center rounded-md bg-secondary/60 px-2 py-0.5 font-mono text-[11.5px] text-foreground/80 break-all"
            >
              {item === null || item === undefined ? '—' : String(item)}
            </li>
          ))}
        </ul>
      );
    }

    return (
      <ul className="space-y-1.5">
        {value.map((item, idx) => (
          <li key={idx} className="flex min-w-0 gap-2.5">
            <span className="font-mono text-[11px] tabular-nums text-muted-foreground">
              {(idx + 1).toString().padStart(2, '0')}
            </span>
            <div className="min-w-0 flex-1">
              <BriefValue value={item} />
            </div>
          </li>
        ))}
      </ul>
    );
  }

  if (typeof value === 'object') {
    const objEntries = Object.entries(value as Record<string, unknown>);
    if (objEntries.length === 0) {
      return <span className="italic text-muted-foreground">No data</span>;
    }
    return (
      <dl className="grid gap-1">
        {objEntries.map(([k, v]) => (
          <div key={k} className="grid min-w-0 grid-cols-[7rem_minmax(0,1fr)] gap-2">
            <dt className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground">
              {camelToTitle(k)}
            </dt>
            <dd className="min-w-0">
              <BriefValue value={v} />
            </dd>
          </div>
        ))}
      </dl>
    );
  }

  return <span>{formatPrimitive(value)}</span>;
}
